import {
  Badge,
  Button,
  makeStyles,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  mergeClasses,
  Text,
  tokens,
  Tooltip,
} from '@fluentui/react-components';
import {
  CloudSyncRegular,
  DocumentBulletListRegular,
  Folder20Regular,
  MicRecordRegular,
  MoreHorizontal20Regular,
  OpenRegular,
  PenSparkleRegular,
  WindowNew20Regular,
} from '@fluentui/react-icons';
import { useDraggable } from '@dnd-kit/core';
import { motion } from 'framer-motion';
import React, { useMemo } from 'react';
import {
  OneDriveSyncStatus,
  RecordingsListItem,
  Status,
  StepsStatus,
} from '@axys-notes/common';
import { useForceOneDriveSyncMutation } from '../../queries/recording';

const useStyles = makeStyles({
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    cursor: 'pointer',
    transitionProperty: 'all',
    transitionDuration: '0.25s',
    transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: 'hidden',
    width: '100%',
    boxSizing: 'border-box',
    boxShadow: `0 1px 3px ${tokens.colorNeutralShadowAmbient}`,
    ':hover': {
      boxShadow: `0 4px 12px ${tokens.colorNeutralShadowAmbient}, 0 2px 4px ${tokens.colorNeutralShadowKey}`,
      transform: 'translateY(-2px)',
    },
  },
  cardActive: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorBrandStroke1}`,
    boxShadow: `0 0 0 1px ${tokens.colorBrandStroke1}, 0 4px 12px ${tokens.colorNeutralShadowAmbient}`,
  },
  cardDragging: {
    opacity: 0.5,
    cursor: 'grabbing',
  },
  cardDisabled: {
    cursor: 'default',
    opacity: 0.7,
    ':hover': {
      boxShadow: `0 1px 3px ${tokens.colorNeutralShadowAmbient}`,
      transform: 'none',
    },
  },
  mainContent: {
    flex: 1,
    width: 0,
    minWidth: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    overflow: 'hidden',
    minWidth: 0,
  },
  title: {
    display: 'block',
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0,
    color: tokens.colorNeutralForeground1,
  },
  summary: {
    display: 'block',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: tokens.lineHeightBase200,
  },
  metadata: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexShrink: 1,
    minWidth: 0,
  },
  dateTime: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    minWidth: '80px',
    paddingRight: tokens.spacingHorizontalM,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    marginRight: tokens.spacingHorizontalS,
  },
  date: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  time: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  duration: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  progressSteps: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusLarge,
    marginLeft: tokens.spacingHorizontalM,
    paddingLeft: tokens.spacingHorizontalM,
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  stepIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    transition: 'background-color 0.2s ease',
    color: tokens.colorNeutralForegroundOnBrand,
    '& svg': {
      fontSize: '18px',
    },
  },
  connector: {
    width: '16px',
    height: '2px',
    minWidth: '16px',
    minHeight: '2px',
    maxWidth: '16px',
    maxHeight: '2px',
    backgroundColor: tokens.colorNeutralStroke2,
    borderRadius: '2px',
    transition: 'background-color 0.3s ease',
    flexShrink: 0,
    flexGrow: 0,
  },
  connectorActive: {
    backgroundColor: tokens.colorBrandBackground,
  },
  connectorCompleted: {
    backgroundColor: tokens.colorPaletteGreenBackground3,
  },
  connectorError: {
    backgroundColor: tokens.colorPaletteRedBackground3,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    opacity: 0,
    transition: 'opacity 0.2s ease, transform 0.2s ease',
    transform: 'translateX(8px)',
  },
  actionsVisible: {
    opacity: 1,
    transform: 'translateX(0)',
  },
  folderBadge: {
    fontSize: tokens.fontSizeBase100,
    borderRadius: tokens.borderRadiusMedium,
  },
});

interface RecordingCardProps {
  recording: RecordingsListItem;
  isActive?: boolean;
  isOneDriveSyncEnabled?: boolean;
  onOpenDrawer: (id: string) => void;
  onOpenNewWindow: (id: string) => void;
  onAssignFolder?: (recordingId: string, currentFolderId?: string) => void;
  index?: number;
}

const StepIndicator = ({
  status,
  icon,
  label,
}: {
  status: Status;
  icon: React.ReactNode;
  label: string;
}) => {
  const styles = useStyles();

  const getStatusBackgroundColor = () => {
    switch (status) {
      case Status.COMPLETED:
        return tokens.colorPaletteGreenBackground3;
      case Status.STARTED:
        return tokens.colorBrandBackground;
      case Status.ERROR:
        return tokens.colorPaletteRedBackground3;
      case Status.PENDING:
      default:
        return tokens.colorNeutralForeground4;
    }
  };

  return (
    <Tooltip content={label} relationship="label">
      <div
        className={styles.stepIcon}
        style={{ backgroundColor: getStatusBackgroundColor() }}
      >
        {icon}
      </div>
    </Tooltip>
  );
};

const ProgressPipeline = ({
  status,
  oneDriveSyncStatus,
  isOneDriveSyncEnabled,
}: {
  status: StepsStatus;
  oneDriveSyncStatus?: OneDriveSyncStatus;
  isOneDriveSyncEnabled?: boolean;
}) => {
  const styles = useStyles();

  const getConnectorClass = (targetStatus: Status) => {
    switch (targetStatus) {
      case Status.COMPLETED:
        return mergeClasses(styles.connector, styles.connectorCompleted);
      case Status.STARTED:
        return mergeClasses(styles.connector, styles.connectorActive);
      case Status.ERROR:
        return mergeClasses(styles.connector, styles.connectorError);
      case Status.PENDING:
      default:
        return styles.connector;
    }
  };

  const getOneDriveStatus = (): Status | null => {
    // If sync is not enabled, don't show the icon
    if (!isOneDriveSyncEnabled) return null;

    // If no status yet, show as pending (gray)
    if (!oneDriveSyncStatus) return Status.PENDING;

    switch (oneDriveSyncStatus) {
      case 'success':
        return Status.COMPLETED;
      case 'pending':
        return Status.STARTED;
      case 'error':
        return Status.ERROR;
    }
  };

  const oneDriveStatus = getOneDriveStatus();

  return (
    <div className={styles.progressSteps}>
      <StepIndicator
        status={status.recorder}
        icon={<MicRecordRegular />}
        label="Enregistrement"
      />
      <div className={getConnectorClass(status.stt)} />
      <StepIndicator
        status={status.stt}
        icon={<PenSparkleRegular />}
        label="Transcription"
      />
      <div className={getConnectorClass(status.summarizer)} />
      <StepIndicator
        status={status.summarizer}
        icon={<DocumentBulletListRegular />}
        label="Compte-rendu"
      />
      {oneDriveStatus !== null && (
        <>
          <div className={getConnectorClass(oneDriveStatus)} />
          <StepIndicator
            status={oneDriveStatus}
            icon={<CloudSyncRegular />}
            label="OneDrive"
          />
        </>
      )}
    </div>
  );
};

export const RecordingCard = ({
  recording,
  isActive,
  isOneDriveSyncEnabled,
  onOpenDrawer,
  onOpenNewWindow,
  onAssignFolder,
  index = 0,
}: RecordingCardProps) => {
  const styles = useStyles();
  const [isHovered, setIsHovered] = React.useState(false);
  const forceOneDriveSync = useForceOneDriveSyncMutation();

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: recording.id,
    data: {
      name: recording.name,
      folderId: recording.folderId,
    },
  });

  const isFullyCompleted = useMemo(
    () =>
      recording.status.recorder === Status.COMPLETED &&
      recording.status.stt === Status.COMPLETED &&
      recording.status.summarizer === Status.COMPLETED,
    [recording.status]
  );

  const minutes = useMemo(() => {
    if (recording.endTime && recording.startTime) {
      return Math.ceil(
        (recording.endTime - recording.startTime) / (60 * 1000)
      );
    }
    return undefined;
  }, [recording.endTime, recording.startTime]);

  const formattedDate = useMemo(() => {
    if (recording.startTime) {
      return new Date(recording.startTime).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    }
    return '...';
  }, [recording.startTime]);

  const formattedTime = useMemo(() => {
    if (recording.startTime) {
      return new Date(recording.startTime).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    return '';
  }, [recording.startTime]);

  return (
    <motion.div
      ref={setNodeRef}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{
        duration: 0.3,
        delay: index * 0.04,
        opacity: { duration: 0.3, ease: 'linear' },
        x: { duration: 0.3, ease: 'easeOut' },
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={mergeClasses(
        styles.card,
        isActive && styles.cardActive,
        isDragging && styles.cardDragging,
        !isFullyCompleted && styles.cardDisabled
      )}
      onClick={isFullyCompleted ? () => onOpenDrawer(recording.id) : undefined}
      {...listeners}
      {...attributes}
    >
      <div className={styles.dateTime}>
        <Text className={styles.date}>{formattedDate}</Text>
        <Text className={styles.time}>{formattedTime}</Text>
        {minutes && <Text className={styles.duration}>{minutes} min</Text>}
      </div>

      <div className={styles.mainContent}>
        <div className={styles.titleRow}>
          <Text className={styles.title}>
            {recording.name || 'Réunion sans titre'}
          </Text>
          {recording.folderName && (
            <Badge
              appearance="outline"
              size="large"
              icon={<Folder20Regular />}
              className={styles.folderBadge}
              style={recording.folderColor ? {
                borderColor: recording.folderColor,
                color: recording.folderColor,
              } : undefined}
            >
              {recording.folderName}
            </Badge>
          )}
        </div>
        <Text className={styles.summary}>
          {recording.summary || 'Aucun résumé disponible...'}
        </Text>
      </div>

      <div className={styles.metadata}>
        <ProgressPipeline
          status={recording.status}
          oneDriveSyncStatus={recording.oneDriveSyncStatus}
          isOneDriveSyncEnabled={isOneDriveSyncEnabled}
        />

        <div
          className={mergeClasses(
            styles.actions,
            isHovered && styles.actionsVisible
          )}
        >
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                appearance="subtle"
                size="small"
                icon={<MoreHorizontal20Regular />}
                onClick={(e) => e.stopPropagation()}
              />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                {isFullyCompleted && (
                  <>
                    <MenuItem
                      icon={<OpenRegular />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenDrawer(recording.id);
                      }}
                    >
                      Ouvrir dans le panneau
                    </MenuItem>
                    <MenuItem
                      icon={<WindowNew20Regular />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenNewWindow(recording.id);
                      }}
                    >
                      Ouvrir dans une nouvelle fenêtre
                    </MenuItem>
                  </>
                )}
                {onAssignFolder && (
                  <MenuItem
                    icon={<Folder20Regular />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAssignFolder(recording.id, recording.folderId);
                    }}
                  >
                    Assigner a un dossier
                  </MenuItem>
                )}
                {isOneDriveSyncEnabled && isFullyCompleted && (
                  <MenuItem
                    icon={<CloudSyncRegular />}
                    disabled={
                      forceOneDriveSync.isPending ||
                      recording.oneDriveSyncStatus === 'pending'
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      forceOneDriveSync.mutate(recording.id);
                    }}
                  >
                    Synchroniser vers OneDrive
                  </MenuItem>
                )}
                {recording.oneDriveSyncStatus === 'success' &&
                  recording.oneDriveReportUrl && (
                    <MenuItem
                      icon={<OpenRegular />}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(recording.oneDriveReportUrl, '_blank');
                      }}
                    >
                      Ouvrir le rapport dans OneDrive
                    </MenuItem>
                  )}
                {recording.oneDriveSyncStatus === 'success' &&
                  recording.oneDriveTranscriptUrl && (
                    <MenuItem
                      icon={<OpenRegular />}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(recording.oneDriveTranscriptUrl, '_blank');
                      }}
                    >
                      Ouvrir la transcription dans OneDrive
                    </MenuItem>
                  )}
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      </div>
    </motion.div>
  );
};
